import {
  getCurrentPlainDate,
  getPlainDateOrNullFrom,
  getPlainDateOrNullFromDate,
  getZonedDateTimeOrNullFromIsoDateString,
  getZonedDateTimeOrNullFromIsoDateTimeString,
  isPlainDateAfter,
  isPlainDateBefore,
} from './temporalUtils';
import type { IIsoDateFields } from './types';

const US_DATE_STRING_LENGTH = 10;

/**
 * Parses a string as an ISO date ('YYYY-MM-DD') and returns an
 * object with its respective fields {year, month, day}, or null if not a valid date string.
 */
export const getIsoDateFieldsFromIsoDateString = (isoString: string): IIsoDateFields | null => {
  const plainDate = getPlainDateOrNullFrom(isoString);
  if (plainDate) {
    return {
      year: plainDate.year,
      month: plainDate.month,
      day: plainDate.day,
    };
  }
  return null;
};

/**
 * Receives an object with {year, month, day} fields and returns the ISO date
 * string or null if invalid.
 */
export const getIsoDateStringFromIsoDateFields = (isoDateFields: IIsoDateFields): string | null => {
  const plainDate = getPlainDateOrNullFrom(isoDateFields);
  if (plainDate) {
    return plainDate.toString();
  }
  return null;
};

/**
 * Parses a string as an ISO date ('YYYY-MM-DD') and returns a Date or null if invalid.
 */
export const getDateFromIsoDateString = (isoString: string): Date | null => {
  const zonedDateTime = getZonedDateTimeOrNullFromIsoDateString(isoString);
  if (zonedDateTime) {
    return new Date(zonedDateTime.epochMilliseconds);
  }
  return null;
};

/**
 * Receives a Date and returns the ISO date string or null if invalid.
 */
export const getIsoDateStringFromDate = (date: Date): string | null => {
  const plainDate = getPlainDateOrNullFromDate(date);
  if (plainDate) {
    return plainDate.toString();
  }
  return null;
};

/**
 * Receives an ISO 8601 long date time string e.g. "2023-02-22T11:25:32.585709-08:00"
 * and returns the short ISO date string "2023-02-22" or null if invalid.
 */
export const getIsoDateStringFromIsoLongDateTimeString = (
  dateTimeString: string
): string | null => {
  const zonedDateTime = getZonedDateTimeOrNullFromIsoDateTimeString(dateTimeString);
  if (zonedDateTime) {
    return zonedDateTime.toPlainDate().toString();
  }
  return null;
};

/** Returns true if string is a valid date, returns false for invalid dates or invalid input. */
export const isIsoDateStringValid = (isoString: string): boolean => {
  return Boolean(getPlainDateOrNullFrom(isoString));
};

/** Returns true if object represents a valid date, returns false for invalid dates or invalid input. */
export const isIsoDateFieldsValid = (isoDateFields: IIsoDateFields): boolean => {
  return Boolean(getPlainDateOrNullFrom(isoDateFields));
};

/** Returns the ISO date string that represents today's date. */
export const getIsoDateStringForToday = (): string => {
  return getCurrentPlainDate().toString();
};

interface IInterval {
  years?: number;
  months?: number;
  days?: number;
}

/**
 * Returns the ISO date string that represents the date `years` , `months` , and `days` prior to today
 */
export function getIsoDateStringBeforeToday(interval: IInterval): string {
  return getCurrentPlainDate().subtract(interval).toString();
}

/**
 * Returns the ISO date string that represents the date `years` , `months` , and `days` after today
 */
export function getIsoDateStringAfterToday(interval: IInterval): string {
  return getCurrentPlainDate().add(interval).toString();
}

/**
 * Adds the specified interval to the given date string and returns the result as a date string.
 * Throws an error if the date is invalid.
 */
export function addToIsoDateString(date: string, interval: IInterval): string {
  const plainDate = getPlainDateOrNullFrom(date);
  if (!plainDate) {
    throw new Error(`Cannot add to invalid ISO date, ${date}`);
  }
  return plainDate.add(interval).toString();
}

/**
 * Subtracts the specified interval from the given date string and returns the result as a date string.
 * Throws an error if the date is invalid.
 */
export function subtractFromIsoDateString(date: string, interval: IInterval): string {
  const plainDate = getPlainDateOrNullFrom(date);
  if (!plainDate) {
    throw new Error(`Cannot subtract from invalid ISO date, ${date}`);
  }
  return plainDate.subtract(interval).toString();
}

/**
 * Returns true if dateString is after dateToCompare, or false if same or before.
 * Throws an error if either date is an invalid IsoDateString.
 */
export const isIsoDateStringAfter = (date: string, dateToCompare: string): boolean => {
  const plainDate = getPlainDateOrNullFrom(date);
  const plainDateToCompare = getPlainDateOrNullFrom(dateToCompare);
  if (!plainDate || !plainDateToCompare) {
    throw new Error('isIsoDateAfter requires two valid IsoDates to compare');
  }
  return isPlainDateAfter(plainDate, plainDateToCompare);
};
/**
 * Returns true if dateString is before dateToCompare, or false if same, or after.
 * Throws an error if either date is an invalid IsoDateString.
 */
export const isIsoDateStringBefore = (date: string, dateToCompare: string): boolean => {
  const plainDate = getPlainDateOrNullFrom(date);
  const plainDateToCompare = getPlainDateOrNullFrom(dateToCompare);
  if (!plainDate || !plainDateToCompare) {
    throw new Error('isIsoDateBefore requires two valid IsoDates to compare');
  }
  return isPlainDateBefore(plainDate, plainDateToCompare);
};

export function getIsoDateStringFromUsDateString(dateString: string): string | null {
  if (dateString.length !== US_DATE_STRING_LENGTH) {
    return null;
  }
  const [month, day, year] = dateString.split('/');
  const isoDateString = `${year}-${month}-${day}`;
  return isIsoDateStringValid(isoDateString) ? isoDateString : null;
}

export function getUsDateStringFromIsoDateString(dateString: string): string | null {
  if (!isIsoDateStringValid(dateString)) {
    return null;
  }
  const [year, month, day] = dateString.split('-');
  return `${month}/${day}/${year}`;
}

/** Gets the ISO datetime string with timezone offset for the start time of the isoDateString date. */
export const getStartOfDayFromIsoDateString = (isoString: string): string => {
  const zonedDateTime = getZonedDateTimeOrNullFromIsoDateString(isoString);
  if (!zonedDateTime) {
    throw new Error(`Cannot get start of day from invalid iso date string, ${isoString}`);
  }
  return zonedDateTime.startOfDay().toString({ timeZoneName: 'never' });
};

/** Gets the ISO datetime string with timezone offset for the end time of the isoDateString date. */
export const getEndOfDayFromIsoDateString = (isoString: string): string => {
  const zonedDateTime = getZonedDateTimeOrNullFromIsoDateString(isoString, {
    plainTime: '23:59:59.999',
  });
  if (!zonedDateTime) {
    throw new Error(`Cannot get start of day from invalid iso date string, ${isoString}`);
  }
  return zonedDateTime.toString({ timeZoneName: 'never' });
};
